import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/yc/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Users, Briefcase, ShieldCheck, X } from "lucide-react";

const ROLES = [
    { r: "brand", t: "I'm a brand", d: "Invite-only. Enter the access code we sent you.", icon: Briefcase, tid: "role-brand" },
    { r: "creator", t: "I'm a creator", d: "Free to apply. Get briefed by paying brands.", icon: Users, tid: "role-creator" },
    { r: "admin", t: "I'm a strategist", d: "Internal team only.", icon: ShieldCheck, tid: "role-admin" },
];

const RoleSelect = () => {
    const { user, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const [submitting, setSubmitting] = useState("");
    const [adminPromptOpen, setAdminPromptOpen] = useState(false);
    const [adminPassword, setAdminPassword] = useState("");
    const [adminError, setAdminError] = useState("");
    const [brandPromptOpen, setBrandPromptOpen] = useState(false);
    const [brandCode, setBrandCode] = useState("");
    const [brandError, setBrandError] = useState("");

    useEffect(() => {
        if (loading) return; // wait for /auth/me before deciding anything
        if (!user) navigate("/login");
        else if (user.role !== "unset") navigate(`/${user.role}/dashboard`);
    }, [user, loading, navigate]);

    const submitRole = async (role, password, accessCode) => {
        setSubmitting(role);
        setAdminError("");
        setBrandError("");
        try {
            const headers = password ? { "X-Admin-Role-Password": password } : undefined;
            await api.post("/auth/role", { role, access_code: accessCode || "" }, headers ? { headers } : undefined);
            await refresh();
            if (role === "brand") navigate("/brand/onboarding");
            else if (role === "creator") navigate("/creator/onboarding");
            else navigate("/admin/dashboard");
        } catch (e) {
            if (role === "admin" && e?.response?.status === 401) {
                setAdminError("Incorrect password.");
            } else if (role === "brand" && e?.response?.status === 401) {
                setBrandError(e.response?.data?.detail || "Invalid access code.");
            } else {
                alert(e.response?.data?.detail || "Could not set role");
            }
        } finally { setSubmitting(""); }
    };

    const choose = (role) => {
        if (role === "admin") {
            setAdminError("");
            setAdminPassword("");
            setAdminPromptOpen(true);
            return;
        }
        if (role === "brand") {
            setBrandError("");
            setBrandCode("");
            setBrandPromptOpen(true);
            return;
        }
        submitRole(role);
    };

    const confirmAdmin = (e) => {
        e.preventDefault();
        if (!adminPassword) return;
        submitRole("admin", adminPassword);
    };

    const confirmBrand = (e) => {
        e.preventDefault();
        if (!brandCode.trim()) return;
        submitRole("brand", undefined, brandCode.trim().toUpperCase());
    };

    const intent = search.get("role");
    useEffect(() => {
        if (loading) return; // wait for /auth/me before auto-selecting
        // Creator intent auto-selects (open signup). Brand is invite-only — always
        // requires an access code, so never auto-submit it. Admin needs a password.
        if (intent === "creator" && user && user.role === "unset") {
            submitRole("creator");
        } else if (intent === "brand" && user && user.role === "unset") {
            setBrandPromptOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intent, user, loading]);

    return (
        <div className="min-h-screen bg-yc-cream">
            <Navbar />
            <section className="max-w-5xl mx-auto px-6 md:px-12 py-16">
                <div className="text-center mb-12">
                    <div className="yc-overline">One last step</div>
                    <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-3">How will you use Curated Chaos?</h1>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {ROLES.map((x) => {
                        const Icon = x.icon;
                        return (
                            <button key={x.r} data-testid={x.tid} disabled={!!submitting} onClick={() => choose(x.r)} className="yc-card p-8 text-left hover:-translate-y-1 hover:shadow-yc-lg transition-all disabled:opacity-50">
                                <Icon className="w-8 h-8 text-yc-blue" />
                                <h3 className="font-heading text-2xl font-bold mt-4">{x.t}</h3>
                                <p className="text-sm text-yc-ink/75 mt-2">{x.d}</p>
                                {submitting === x.r && <div className="text-xs text-yc-blue mt-2">Setting up…</div>}
                            </button>
                        );
                    })}
                </div>
            </section>

            {brandPromptOpen && (
                <div className="fixed inset-0 bg-yc-ink/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="brand-code-modal">
                    <div className="bg-white border-2 border-yc-ink rounded-2xl max-w-md w-full p-6 relative">
                        <button
                            type="button"
                            onClick={() => setBrandPromptOpen(false)}
                            className="absolute top-3 right-3 p-2 hover:bg-yc-cream rounded-full transition-colors"
                            data-testid="brand-code-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="yc-overline">Invite-only</div>
                        <h2 className="font-heading text-2xl font-black tracking-tight mt-1">Enter your access code</h2>
                        <p className="text-sm text-yc-ink/70 mt-2">Brand membership is invite-only. Enter the unique code we sent you after your enquiry.</p>
                        <form onSubmit={confirmBrand} className="mt-5 space-y-3">
                            <input
                                autoFocus
                                value={brandCode}
                                onChange={(e) => setBrandCode(e.target.value.toUpperCase())}
                                placeholder="BRAND-XXXXXX"
                                autoComplete="off"
                                data-testid="brand-code-input"
                                className="w-full border-2 border-yc-ink rounded-lg px-4 py-3 text-base bg-yc-cream focus:outline-none focus:bg-white focus:border-yc-blue uppercase tracking-widest font-bold"
                            />
                            {brandError && <div className="text-sm text-yc-pink font-semibold" data-testid="brand-code-error">{brandError}</div>}
                            <button
                                type="submit"
                                disabled={!brandCode.trim() || submitting === "brand"}
                                data-testid="brand-code-submit"
                                className="yc-btn-primary w-full disabled:opacity-50"
                            >
                                {submitting === "brand" ? "Verifying…" : "Unlock brand account"}
                            </button>
                        </form>
                        <p className="text-xs text-yc-ink/50 mt-4">
                            Don&apos;t have a code? <a href="/brands/join" className="text-yc-blue font-semibold underline">Request access here</a>.
                        </p>
                    </div>
                </div>
            )}

            {adminPromptOpen && (
                <div className="fixed inset-0 bg-yc-ink/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="admin-password-modal">
                    <div className="bg-white border-2 border-yc-ink rounded-2xl max-w-md w-full p-6 relative">
                        <button
                            type="button"
                            onClick={() => setAdminPromptOpen(false)}
                            className="absolute top-3 right-3 p-2 hover:bg-yc-cream rounded-full transition-colors"
                            data-testid="admin-password-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="yc-overline text-yc-pink">Strategist access</div>
                        <h2 className="font-heading text-2xl font-black tracking-tight mt-1">Enter admin password</h2>
                        <p className="text-sm text-yc-ink/70 mt-2">The strategist role is restricted to internal Curated Chaos team members. Enter the shared password to continue.</p>
                        <form onSubmit={confirmAdmin} className="mt-5 space-y-3">
                            <input
                                type="password"
                                autoFocus
                                value={adminPassword}
                                onChange={(e) => setAdminPassword(e.target.value)}
                                placeholder="Password"
                                autoComplete="off"
                                data-testid="admin-password-input"
                                className="w-full border-2 border-yc-ink rounded-lg px-4 py-3 text-base bg-yc-cream focus:outline-none focus:bg-white focus:border-yc-blue"
                            />
                            {adminError && <div className="text-sm text-yc-pink font-semibold" data-testid="admin-password-error">{adminError}</div>}
                            <button
                                type="submit"
                                disabled={!adminPassword || submitting === "admin"}
                                data-testid="admin-password-submit"
                                className="yc-btn-primary w-full disabled:opacity-50"
                            >
                                {submitting === "admin" ? "Verifying…" : "Continue as strategist"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleSelect;
